import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';

const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_REFERRAL_RECAPTCHA_SITE_KEY ||
  '6LcS7WkpAAAAAOi-iLkYRlQXBT1c_9TVvmaY3jQG';
const RECAPTCHA_SCRIPT_ID = 'referral-recaptcha-script';
const RECAPTCHA_CONTAINER_ID = 'referral-recaptcha-container';
const LOCAL_CAPTCHA_BYPASS = process.env.NODE_ENV !== 'production';

const FALLBACK_HUBS = [
  { value: 'hub1', label: 'Hub #1 - Alhambra Court', enabled: true },
  { value: 'hub2', label: 'Hub #2', enabled: true },
  { value: 'hub3', label: 'Hub #3', enabled: true },
  { value: 'maternal_health', label: 'Maternal Health', enabled: true },
];

const FALLBACK_SOURCES = [
  'Public defender',
  'Alternative public defender',
  'IDCO',
  'Private/Other',
];

const GET_REFERRAL_FORM_METADATA = gql`
  query ReferralFormMetadata {
    referralFormMetadata {
      isOpen
      sourceOptions
      hubOptions {
        value
        label
        enabled
      }
    }
  }
`;

const SUBMIT_REFERRAL_FORM_PUBLIC = gql`
  mutation SubmitReferralFormPublic($input: ReferralPublicSubmissionInput!) {
    submitReferralFormPublic(input: $input) {
      message
      responseCode
      responseID
      extra
    }
  }
`;

const REFERRAL_LOOKUP_REQUIRED_FIELDS = gql`
  query ReferralLookupRequiredFields($bookingNumber: String, $caseNumber: String, $fullName: String) {
    referralLookupRequiredFields(bookingNumber: $bookingNumber, caseNumber: $caseNumber, fullName: $fullName) {
      found
      requiredFields
      message
    }
  }
`;

type YesNoValue = 'yes' | 'no' | '';

type ReferralFormState = {
  hub: string;
  referrerFirstName: string;
  referrerLastName: string;
  referrerEmail: string;
  referrerSource: string;
  firstName: string;
  lastName: string;
  bookingNumber: string;
  caseNumber: string;
  homeless: YesNoValue;
  homelessDesc: string;
  mentalHealthDisorder: YesNoValue;
  mentalHealthDisorderDesc: string;
  veteran: YesNoValue;
  veteranDesc: string;
  regionalCenter: YesNoValue;
  twoPriorStrikes: YesNoValue;
  isAb109: YesNoValue;
  parole: YesNoValue;
  paroleDesc: string;
  isprePlea: YesNoValue;
  additionalNotes: string;
};

type HubOption = {
  value: string;
  label: string;
  enabled: boolean;
};

type ReferralMetadataData = {
  referralFormMetadata: {
    isOpen: boolean;
    sourceOptions: string[];
    hubOptions: HubOption[];
  };
};

type ReferralLookupData = {
  referralLookupRequiredFields: {
    found: boolean;
    requiredFields: string[];
    message: string;
  };
};

declare global {
  interface Window {
    grecaptcha?: {
      render: (container: string | HTMLElement, parameters: Record<string, any>) => number;
      reset: (widgetId?: number) => void;
    };
  }
}

const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader>) => {
      const result = `${event.target?.result || ''}`;
      const [, base64] = result.split(',');
      if (!base64) {
        reject(new Error('Unable to parse file payload'));
        return;
      }
      resolve(base64);
    };

    reader.onerror = () => {
      reject(new Error('Unable to read file'));
    };

    reader.readAsDataURL(file);
  });
};

const emptyFormState: ReferralFormState = {
  hub: '',
  referrerFirstName: '',
  referrerLastName: '',
  referrerEmail: '',
  referrerSource: '',
  firstName: '',
  lastName: '',
  bookingNumber: '',
  caseNumber: '',
  homeless: '',
  homelessDesc: '',
  mentalHealthDisorder: '',
  mentalHealthDisorderDesc: '',
  veteran: '',
  veteranDesc: '',
  regionalCenter: '',
  twoPriorStrikes: '',
  isAb109: '',
  parole: '',
  paroleDesc: '',
  isprePlea: '',
  additionalNotes: '',
};

type YesNoFieldProps = {
  id: string;
  label: string;
  value: YesNoValue;
  onChange: (value: YesNoValue) => void;
};

const YesNoField: React.FC<YesNoFieldProps> = ({ id, label, value, onChange }) => {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-4 pt-1">
        <label className="flex items-center gap-2 text-sm">
          <input
            id={`${id}-yes`}
            type="radio"
            name={id}
            value="yes"
            checked={value === 'yes'}
            onChange={() => onChange('yes')}
            required
          />
          Yes
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            id={`${id}-no`}
            type="radio"
            name={id}
            value="no"
            checked={value === 'no'}
            onChange={() => onChange('no')}
            required
          />
          No
        </label>
      </div>
    </div>
  );
};

export const ReferralFormPage: React.FC = () => {
  const [formState, setFormState] = useState<ReferralFormState>(emptyFormState);
  const [arrestReportFile, setArrestReportFile] = useState<File | null>(null);
  const [rapSheetFile, setRapSheetFile] = useState<File | null>(null);
  const [mentalHealthFile, setMentalHealthFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaLoadError, setCaptchaLoadError] = useState('');
  const [captchaReady, setCaptchaReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lookupDialogOpen, setLookupDialogOpen] = useState(false);
  const [lookupResult, setLookupResult] = useState<ReferralLookupData['referralLookupRequiredFields'] | null>(null);

  const widgetIdRef = useRef<number | null>(null);

  const { data: metadataData, loading: metadataLoading } = useQuery<ReferralMetadataData>(
    GET_REFERRAL_FORM_METADATA,
    { fetchPolicy: 'cache-and-network' }
  );
  const [submitReferralFormPublic] = useMutation(SUBMIT_REFERRAL_FORM_PUBLIC);
  const [lookupRequiredFields, { loading: lookupLoading }] = useLazyQuery<ReferralLookupData>(
    REFERRAL_LOOKUP_REQUIRED_FIELDS,
    { fetchPolicy: 'no-cache' }
  );

  const isFormOpen = metadataData?.referralFormMetadata?.isOpen !== false;

  const hubOptions = useMemo(() => {
    const values = metadataData?.referralFormMetadata?.hubOptions;
    if (Array.isArray(values) && values.length > 0) {
      return values;
    }
    return FALLBACK_HUBS;
  }, [metadataData]);

  const sourceOptions = useMemo(() => {
    const values = metadataData?.referralFormMetadata?.sourceOptions;
    if (Array.isArray(values) && values.length > 0) {
      return values;
    }
    return FALLBACK_SOURCES;
  }, [metadataData]);

  useEffect(() => {
    if (LOCAL_CAPTCHA_BYPASS) {
      setCaptchaToken('local-bypass');
      setCaptchaReady(true);
      setCaptchaLoadError('');
      return;
    }

    document.body.classList.add('referral-public-page');
    return () => {
      document.body.classList.remove('referral-public-page');
    };
  }, []);

  useEffect(() => {
    if (LOCAL_CAPTCHA_BYPASS) {
      return;
    }

    if (!RECAPTCHA_SITE_KEY) {
      setCaptchaLoadError('Captcha is not configured.');
      return;
    }

    let isCancelled = false;
    let retryTimer: number | null = null;

    const clearRetry = () => {
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const renderCaptcha = () => {
      if (isCancelled) {
        return;
      }

      if (!window.grecaptcha || widgetIdRef.current !== null) {
        return;
      }

      const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
      if (!container) {
        clearRetry();
        retryTimer = window.setTimeout(renderCaptcha, 100);
        return;
      }

      try {
        widgetIdRef.current = window.grecaptcha.render(container, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token: string) => setCaptchaToken(token),
          'expired-callback': () => setCaptchaToken(''),
          'error-callback': () => setCaptchaToken(''),
        });
        setCaptchaReady(true);
        setCaptchaLoadError('');
      } catch (_error) {
        setCaptchaLoadError('Unable to initialize captcha.');
      }
    };

    if (window.grecaptcha) {
      renderCaptcha();
      return () => {
        isCancelled = true;
        clearRetry();
      };
    }

    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', renderCaptcha);
      renderCaptcha();
      return () => {
        isCancelled = true;
        clearRetry();
        existing.removeEventListener('load', renderCaptcha);
      };
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => renderCaptcha();
    script.onerror = () => setCaptchaLoadError('Unable to load captcha.');
    document.body.appendChild(script);

    return () => {
      isCancelled = true;
      clearRetry();
    };
  }, []);

  const setField = (field: keyof ReferralFormState, value: string) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormState(emptyFormState);
    setArrestReportFile(null);
    setRapSheetFile(null);
    setMentalHealthFile(null);
    setFileInputKey((value) => value + 1);
    setCaptchaToken(LOCAL_CAPTCHA_BYPASS ? 'local-bypass' : '');
    setSubmitted(false);

    if (window.grecaptcha && widgetIdRef.current !== null) {
      window.grecaptcha.reset(widgetIdRef.current);
    }
  };

  const validateBeforeSubmit = () => {
    const requiredTextFields: Array<[keyof ReferralFormState, string]> = [
      ['hub', 'Hub'],
      ['referrerFirstName', 'Referrer first name'],
      ['referrerLastName', 'Referrer last name'],
      ['referrerEmail', 'Referrer email'],
      ['referrerSource', 'Referral source'],
      ['firstName', 'Client first name'],
      ['lastName', 'Client last name'],
      ['bookingNumber', 'Booking number'],
      ['caseNumber', 'Case number'],
    ];

    for (const [field, label] of requiredTextFields) {
      if (!`${formState[field] || ''}`.trim()) {
        throw new Error(`${label} is required`);
      }
    }

    const requiredBooleanFields: Array<[keyof ReferralFormState, string]> = [
      ['homeless', 'Homeless'],
      ['mentalHealthDisorder', 'Serious mental health disorder'],
      ['veteran', 'Veteran'],
      ['regionalCenter', 'Regional center'],
      ['twoPriorStrikes', 'Two prior strikes'],
      ['isAb109', 'AB109'],
      ['parole', 'Parole'],
      ['isprePlea', 'Pre-plea'],
    ];

    for (const [field, label] of requiredBooleanFields) {
      const value = formState[field];
      if (value !== 'yes' && value !== 'no') {
        throw new Error(`${label} must be answered`);
      }
    }

    if (formState.homeless === 'yes' && !formState.homelessDesc.trim()) {
      throw new Error('Homeless description is required');
    }

    if (formState.mentalHealthDisorder === 'yes' && !formState.mentalHealthDisorderDesc.trim()) {
      throw new Error('Serious mental health disorder description is required');
    }

    if (formState.veteran === 'yes' && !formState.veteranDesc.trim()) {
      throw new Error('Veteran description is required');
    }

    if (formState.parole === 'yes' && !formState.paroleDesc.trim()) {
      throw new Error('Parole description is required');
    }

    if (!arrestReportFile) {
      throw new Error('Arrest report PDF is required');
    }

    if (!rapSheetFile) {
      throw new Error('RAP sheet PDF is required');
    }

    if (arrestReportFile.type !== 'application/pdf') {
      throw new Error('Arrest report must be a PDF');
    }

    if (rapSheetFile.type !== 'application/pdf') {
      throw new Error('RAP sheet must be a PDF');
    }

    if (mentalHealthFile && mentalHealthFile.type !== 'application/pdf') {
      throw new Error('Mental health file must be a PDF');
    }

    if (!captchaToken) {
      throw new Error('Complete captcha before submitting');
    }
  };

  const handleLookupRequiredFields = async () => {
    const bookingNumber = formState.bookingNumber.trim();
    const caseNumber = formState.caseNumber.trim();
    const fullName = `${formState.firstName.trim()} ${formState.lastName.trim()}`.trim();

    if (!bookingNumber && !caseNumber && !fullName) {
      toast.error('Enter booking number, case number, or client name before running lookup.');
      return;
    }

    try {
      const response = await lookupRequiredFields({
        variables: {
          bookingNumber: bookingNumber || null,
          caseNumber: caseNumber || null,
          fullName: fullName || null,
        },
      });

      const payload = response.data?.referralLookupRequiredFields;
      if (!payload) {
        throw new Error('Unable to run lookup');
      }

      setLookupResult(payload);
      setLookupDialogOpen(true);
    } catch (error: any) {
      toast.error(error?.message || 'Unable to lookup required fields');
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) {
      return;
    }

    setSubmitting(true);

    try {
      validateBeforeSubmit();

      const arrestReportFileBase64 = await readFileAsBase64(arrestReportFile as File);
      const rapSheetFileBase64 = await readFileAsBase64(rapSheetFile as File);
      const mentalHealthFileBase64 = mentalHealthFile
        ? await readFileAsBase64(mentalHealthFile)
        : null;

      const response = await submitReferralFormPublic({
        variables: {
          input: {
            hub: formState.hub,
            referrerFirstName: formState.referrerFirstName.trim(),
            referrerLastName: formState.referrerLastName.trim(),
            referrerEmail: formState.referrerEmail.trim(),
            referrerSource: formState.referrerSource,
            firstName: formState.firstName.trim(),
            lastName: formState.lastName.trim(),
            bookingNumber: formState.bookingNumber.trim(),
            caseNumber: formState.caseNumber.trim(),
            homeless: formState.homeless,
            homelessDesc: formState.homelessDesc.trim(),
            mentalHealthDisorder: formState.mentalHealthDisorder,
            mentalHealthDisorderDesc: formState.mentalHealthDisorderDesc.trim(),
            veteran: formState.veteran,
            veteranDesc: formState.veteranDesc.trim(),
            regionalCenter: formState.regionalCenter,
            twoPriorStrikes: formState.twoPriorStrikes,
            isAb109: formState.isAb109,
            parole: formState.parole,
            paroleDesc: formState.paroleDesc.trim(),
            isprePlea: formState.isprePlea,
            additionalNotes: formState.additionalNotes.trim(),
            captchaToken,
            arrestReportFile: {
              name: arrestReportFile?.name,
              mimeType: arrestReportFile?.type || 'application/pdf',
              base64: arrestReportFileBase64,
            },
            rapSheetFile: {
              name: rapSheetFile?.name,
              mimeType: rapSheetFile?.type || 'application/pdf',
              base64: rapSheetFileBase64,
            },
            mentalHealthFile: mentalHealthFile
              ? {
                  name: mentalHealthFile.name,
                  mimeType: mentalHealthFile.type || 'application/pdf',
                  base64: mentalHealthFileBase64,
                }
              : null,
          },
        },
      });

      const payload = response?.data?.submitReferralFormPublic;
      if (Number(payload?.responseCode) !== 200) {
        throw new Error(payload?.message || 'Unable to submit referral');
      }

      toast.success('Referral submitted successfully.');
      setSubmitted(true);
      resetForm();
    } catch (error: any) {
      toast.error(error?.message || 'Unable to submit referral');
      if (window.grecaptcha && widgetIdRef.current !== null) {
        window.grecaptcha.reset(widgetIdRef.current);
      }
      setCaptchaToken(LOCAL_CAPTCHA_BYPASS ? 'local-bypass' : '');
    }

    setSubmitting(false);
  };

  if (metadataLoading && !metadataData) {
    return (
      <div className="referral-print-page min-h-screen bg-gray-50 px-4 py-8">
        <div className="referral-print-container mx-auto max-w-4xl rounded border bg-white p-6 text-sm text-gray-700">
          Loading referral form...
        </div>
      </div>
    );
  }

  if (!isFormOpen) {
    return (
      <div className="referral-print-page min-h-screen bg-gray-50 px-4 py-8">
        <div className="referral-print-container mx-auto max-w-4xl rounded border bg-white p-6">
          <h1 className="text-xl font-semibold text-gray-900">Referral Form</h1>
          <p className="mt-2 text-sm text-gray-700">The referral form is currently closed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="referral-print-page min-h-screen bg-gray-50">
      <div className="referral-print-container mx-auto max-w-4xl space-y-4 px-4 py-8">
        <Card className="referral-print-card">
          <CardHeader>
            <CardTitle>ODR Referral Form</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <section className="referral-print-section space-y-4">
                <h2 className="text-base font-semibold text-gray-900">Referring Party Information</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="ref-hub">Location / Hub</Label>
                    <select
                      id="ref-hub"
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={formState.hub}
                      onChange={(event) => setField('hub', event.target.value)}
                      required
                    >
                      <option value="">Select hub</option>
                      {hubOptions
                        .filter((option) => option.enabled)
                        .map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ref-source">Referral source</Label>
                    <select
                      id="ref-source"
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={formState.referrerSource}
                      onChange={(event) => setField('referrerSource', event.target.value)}
                      required
                    >
                      <option value="">Select source</option>
                      {sourceOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="ref-first">Your first name</Label>
                    <Input
                      id="ref-first"
                      value={formState.referrerFirstName}
                      onChange={(event) => setField('referrerFirstName', event.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ref-last">Your last name</Label>
                    <Input
                      id="ref-last"
                      value={formState.referrerLastName}
                      onChange={(event) => setField('referrerLastName', event.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ref-email">Your email</Label>
                    <Input
                      id="ref-email"
                      type="email"
                      value={formState.referrerEmail}
                      onChange={(event) => setField('referrerEmail', event.target.value)}
                      required
                    />
                  </div>
                </div>
              </section>

              <section className="referral-print-section space-y-4">
                <h2 className="text-base font-semibold text-gray-900">Client Information</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="client-first">Client first name</Label>
                    <Input
                      id="client-first"
                      value={formState.firstName}
                      onChange={(event) => setField('firstName', event.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="client-last">Client last name</Label>
                    <Input
                      id="client-last"
                      value={formState.lastName}
                      onChange={(event) => setField('lastName', event.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="booking-number">Booking number</Label>
                    <Input
                      id="booking-number"
                      value={formState.bookingNumber}
                      onChange={(event) => setField('bookingNumber', event.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="case-number">Case number(s)</Label>
                    <Input
                      id="case-number"
                      value={formState.caseNumber}
                      onChange={(event) => setField('caseNumber', event.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="button" variant="outline" onClick={handleLookupRequiredFields} disabled={lookupLoading}>
                    {lookupLoading ? 'Checking...' : 'Check Missing Fields'}
                  </Button>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <YesNoField
                    id="q-homeless"
                    label="Homeless"
                    value={formState.homeless}
                    onChange={(value) => setField('homeless', value)}
                  />
                  <YesNoField
                    id="q-mental"
                    label="Serious mental health disorder"
                    value={formState.mentalHealthDisorder}
                    onChange={(value) => setField('mentalHealthDisorder', value)}
                  />
                </div>

                {formState.homeless === 'yes' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="q-homeless-desc">Homeless description</Label>
                    <Textarea
                      id="q-homeless-desc"
                      rows={3}
                      value={formState.homelessDesc}
                      onChange={(event) => setField('homelessDesc', event.target.value)}
                      required
                    />
                  </div>
                )}

                {formState.mentalHealthDisorder === 'yes' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="q-mental-desc">Serious mental health disorder description</Label>
                    <Textarea
                      id="q-mental-desc"
                      rows={3}
                      value={formState.mentalHealthDisorderDesc}
                      onChange={(event) => setField('mentalHealthDisorderDesc', event.target.value)}
                      required
                    />
                  </div>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  <YesNoField
                    id="q-veteran"
                    label="Veteran"
                    value={formState.veteran}
                    onChange={(value) => setField('veteran', value)}
                  />
                  <YesNoField
                    id="q-regional"
                    label="Regional Center"
                    value={formState.regionalCenter}
                    onChange={(value) => setField('regionalCenter', value)}
                  />
                </div>

                {formState.veteran === 'yes' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="q-veteran-desc">Veteran description</Label>
                    <Textarea
                      id="q-veteran-desc"
                      rows={3}
                      value={formState.veteranDesc}
                      onChange={(event) => setField('veteranDesc', event.target.value)}
                      required
                    />
                  </div>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  <YesNoField
                    id="q-strikes"
                    label="Does the client have 2 prior strikes?"
                    value={formState.twoPriorStrikes}
                    onChange={(value) => setField('twoPriorStrikes', value)}
                  />
                  <YesNoField
                    id="q-ab109"
                    label="Is the client AB109?"
                    value={formState.isAb109}
                    onChange={(value) => setField('isAb109', value)}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <YesNoField
                    id="q-parole"
                    label="Is the client on parole?"
                    value={formState.parole}
                    onChange={(value) => setField('parole', value)}
                  />
                  <YesNoField
                    id="q-preplea"
                    label="Is the case being referred pre-plea?"
                    value={formState.isprePlea}
                    onChange={(value) => setField('isprePlea', value)}
                  />
                </div>

                {formState.parole === 'yes' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="q-parole-desc">Parole details</Label>
                    <Textarea
                      id="q-parole-desc"
                      rows={3}
                      value={formState.paroleDesc}
                      onChange={(event) => setField('paroleDesc', event.target.value)}
                      required
                    />
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="ref-additional-notes">Additional notes</Label>
                  <Textarea
                    id="ref-additional-notes"
                    rows={5}
                    value={formState.additionalNotes}
                    onChange={(event) => setField('additionalNotes', event.target.value)}
                  />
                </div>
              </section>

              <section className="referral-print-section space-y-4">
                <h2 className="text-base font-semibold text-gray-900">Required Attachments</h2>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="ref-arrest-report">Upload PDF of Arrest Report</Label>
                    <Input
                      key={`arrest-${fileInputKey}`}
                      id="ref-arrest-report"
                      type="file"
                      accept="application/pdf"
                      onChange={(event) => setArrestReportFile(event.target.files?.[0] || null)}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ref-rap-sheet">Upload PDF of RAP Sheet</Label>
                    <Input
                      key={`rap-${fileInputKey}`}
                      id="ref-rap-sheet"
                      type="file"
                      accept="application/pdf"
                      onChange={(event) => setRapSheetFile(event.target.files?.[0] || null)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="ref-mental-health">Upload PDF of Mental Health Records (optional)</Label>
                  <Input
                    key={`mental-${fileInputKey}`}
                    id="ref-mental-health"
                    type="file"
                    accept="application/pdf"
                    onChange={(event) => setMentalHealthFile(event.target.files?.[0] || null)}
                  />
                </div>
              </section>

              <section className="referral-print-section space-y-1.5">
                <Label>Captcha</Label>
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  {LOCAL_CAPTCHA_BYPASS
                    ? <p className="text-sm text-gray-600">Captcha bypassed in local development.</p>
                    : <div id={RECAPTCHA_CONTAINER_ID} />}
                </div>
                {!captchaReady && !captchaLoadError && (
                  <p className="text-sm text-gray-600">Loading captcha...</p>
                )}
                {captchaLoadError && <p className="text-sm text-red-600">{captchaLoadError}</p>}
              </section>

              <div className="flex items-center gap-2">
                <Button type="submit" disabled={submitting || !captchaReady}>
                  {submitting ? 'Submitting...' : 'Submit Referral'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} disabled={submitting}>
                  Reset
                </Button>
              </div>

              {submitted && (
                <p className="text-sm text-green-700">Referral submitted successfully.</p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>

      <Dialog open={lookupDialogOpen} onOpenChange={setLookupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Referral Lookup</DialogTitle>
          </DialogHeader>
          {!lookupResult && (
            <p className="text-sm text-gray-700">No lookup result available.</p>
          )}
          {lookupResult && (
            <div className="space-y-3 text-sm text-gray-800">
              <p>{lookupResult.message}</p>
              {lookupResult.found && (
                <div>
                  <p className="font-semibold">Required fields to collect:</p>
                  {(lookupResult.requiredFields || []).length > 0 ? (
                    <ul className="list-disc pl-5">
                      {lookupResult.requiredFields.map((field) => (
                        <li key={field}>{field}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>No additional fields are required at this time.</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" onClick={() => setLookupDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReferralFormPage;
